import { MODULES, type ModuleMeta } from './modules/_manifest.js';
import type { Region } from './hooks/useRegion.js';

export type CapabilityArea = 'monitor' | 'incidents' | 'analytics' | 'operations' | 'account';
export type CapabilityState = 'available' | 'permission_required' | 'region_unavailable' | 'unknown';

export interface Capability {
	key: string;
	moduleKey?: string;
	route: string;
	labelKey: string;
	area: CapabilityArea;
	cn: CapabilityState;
	apac: CapabilityState;
	priority?: ModuleMeta['priority'];
}

const BOTH: CapabilityState = 'available';

export const CAPABILITIES: Capability[] = [
	{ key: 'monitor', route: '/m/monitor', labelKey: 'tabbar.monitor', area: 'monitor', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'store', moduleKey: 'store', route: '/store', labelKey: 'modules.store', area: 'monitor', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'device', moduleKey: 'device', route: '/device', labelKey: 'modules.device', area: 'monitor', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'live', moduleKey: 'live', route: '/live', labelKey: 'modules.live', area: 'monitor', cn: BOTH, apac: 'region_unavailable', priority: 'P0' },

	{ key: 'incidents', route: '/m/incidents', labelKey: 'tabbar.incidents', area: 'incidents', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'event', moduleKey: 'event', route: '/event', labelKey: 'modules.event', area: 'incidents', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'alert', moduleKey: 'alert', route: '/alert', labelKey: 'modules.alert', area: 'incidents', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'message', moduleKey: 'message', route: '/message', labelKey: 'modules.message', area: 'incidents', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'figurewarning', moduleKey: 'figurewarning', route: '/figurewarning', labelKey: 'modules.figurewarning', area: 'incidents', cn: BOTH, apac: 'region_unavailable', priority: 'P1' },
	{ key: 'guard', moduleKey: 'guard', route: '/guard', labelKey: 'modules.guard', area: 'incidents', cn: BOTH, apac: 'region_unavailable', priority: 'P2' },
	{ key: 'leavepost', moduleKey: 'leavepost', route: '/leavepost', labelKey: 'modules.leavepost', area: 'incidents', cn: BOTH, apac: 'permission_required', priority: 'P1' },

	{ key: 'analytics', route: '/m/analytics', labelKey: 'tabbar.analytics', area: 'analytics', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'member', moduleKey: 'member', route: '/member', labelKey: 'modules.member', area: 'analytics', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'customer', moduleKey: 'customer', route: '/customer', labelKey: 'modules.customer', area: 'analytics', cn: BOTH, apac: 'permission_required', priority: 'P1' },
	{ key: 'traffic', moduleKey: 'traffic', route: '/traffic', labelKey: 'modules.traffic', area: 'analytics', cn: BOTH, apac: 'permission_required', priority: 'P1' },
	{ key: 'heatmap', moduleKey: 'heatmap', route: '/heatmap', labelKey: 'modules.heatmap', area: 'analytics', cn: BOTH, apac: 'permission_required', priority: 'P1' },
	{ key: 'datawarn', moduleKey: 'datawarn', route: '/datawarn', labelKey: 'modules.datawarn', area: 'analytics', cn: BOTH, apac: 'region_unavailable', priority: 'P3' },
	{ key: 'cloudstorage', moduleKey: 'cloudstorage', route: '/cloudstorage', labelKey: 'modules.cloudstorage', area: 'analytics', cn: BOTH, apac: BOTH, priority: 'P3' },

	{ key: 'operations', route: '/m/operations', labelKey: 'tabbar.operations', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P0' },
	{ key: 'patrol', moduleKey: 'patrol', route: '/patrol', labelKey: 'modules.patrol', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'aipatrol', moduleKey: 'aipatrol', route: '/aipatrol', labelKey: 'modules.aipatrol', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'scan', moduleKey: 'scan', route: '/scan', labelKey: 'modules.scan', area: 'operations', cn: BOTH, apac: 'permission_required', priority: 'P2' },
	{ key: 'sharedevice', moduleKey: 'sharedevice', route: '/sharedevice', labelKey: 'modules.sharedevice', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P3' },
	{ key: 'pos', moduleKey: 'pos', route: '/pos', labelKey: 'modules.pos', area: 'operations', cn: BOTH, apac: 'permission_required', priority: 'P2' },
	{ key: 'smartlistening', moduleKey: 'smartlistening', route: '/smart-listening', labelKey: 'modules.smartlistening', area: 'operations', cn: 'region_unavailable', apac: 'permission_required', priority: 'P2' },
	{ key: 'phonelive', moduleKey: 'phonelive', route: '/phonelive', labelKey: 'modules.phonelive', area: 'operations', cn: BOTH, apac: 'permission_required', priority: 'P3' },
	{ key: 'evaluation', moduleKey: 'evaluation', route: '/evaluation', labelKey: 'modules.evaluation', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'inspect', moduleKey: 'inspect', route: '/inspect', labelKey: 'modules.inspect', area: 'operations', cn: BOTH, apac: BOTH, priority: 'P1' },
	{ key: 'reportaudit', moduleKey: 'reportaudit', route: '/reportaudit', labelKey: 'modules.reportaudit', area: 'operations', cn: BOTH, apac: 'region_unavailable', priority: 'P1' },
	{ key: 'rewardpunish', moduleKey: 'rewardpunish', route: '/rewardpunish', labelKey: 'modules.rewardpunish', area: 'operations', cn: BOTH, apac: 'permission_required', priority: 'P2' },
	{ key: 'staff', moduleKey: 'staff', route: '/staff', labelKey: 'modules.staff', area: 'operations', cn: BOTH, apac: 'permission_required', priority: 'P2' },
	{ key: 'wechatvip', moduleKey: 'wechatvip', route: '/wechatvip', labelKey: 'modules.wechatvip', area: 'operations', cn: BOTH, apac: 'region_unavailable', priority: 'P1' },
	{ key: 'university', moduleKey: 'university', route: '/university', labelKey: 'modules.university', area: 'operations', cn: BOTH, apac: 'region_unavailable', priority: 'P3' },

	{ key: 'account', route: '/m/account', labelKey: 'tabbar.account', area: 'account', cn: BOTH, apac: BOTH, priority: 'P0' },
];

const BY_MODULE = new Map(CAPABILITIES.filter((c) => c.moduleKey).map((c) => [c.moduleKey!, c]));

export function capabilityState(capability: Capability, region: Region): CapabilityState {
	return region === 'apac' ? capability.apac : capability.cn;
}

export function capabilityForModule(moduleKey: string): Capability | undefined {
	return BY_MODULE.get(moduleKey);
}

export function moduleState(module: ModuleMeta, region: Region): CapabilityState {
	const cap = capabilityForModule(module.key);
	if (cap) return capabilityState(cap, region);
	return region === 'apac' && module.cnOnly ? 'region_unavailable' : 'available';
}

export function moduleVisibleInNavigation(module: ModuleMeta, region: Region): boolean {
	const state = moduleState(module, region);
	return state === 'available' || state === 'permission_required';
}

export function capabilitiesForArea(area: CapabilityArea, region: Region, states: CapabilityState[] = ['available']): Capability[] {
	const allowed = new Set(states);
	return CAPABILITIES.filter((cap) => cap.area === area && allowed.has(capabilityState(cap, region)));
}

export function moduleByKey(moduleKey: string): ModuleMeta | undefined {
	return MODULES.find((m) => m.key === moduleKey);
}
